import { RefObject, useLayoutEffect, useRef } from 'react'
import { agendaScrollTarget } from './model/agenda-scroll'

/** How many items after the current one stay in view. */
const LOOKAHEAD = 3

/** Space above the current item when it is lined up with the list's top. */
const PADDING = 8

/** Marks each agenda item in the list. */
const ITEM_SELECTOR = '[data-agenda-item]'

/** Marks a day heading, which belongs to the item right under it. */
const DAY_ATTRIBUTE = 'data-agenda-day'

const prefersReducedMotion = (): boolean =>
	typeof window.matchMedia === 'function' &&
	window.matchMedia('(prefers-reduced-motion: reduce)').matches

/** The item together with the day heading above it, when it has one. */
const measure = (item: HTMLElement): { top: number; height: number } => {
	const above = item.previousElementSibling
	const first =
		above instanceof HTMLElement && above.hasAttribute(DAY_ATTRIBUTE)
			? above
			: item
	const top = first.getBoundingClientRect().top
	return { top, height: item.getBoundingClientRect().bottom - top }
}

/** The trainer taking hold of the list: it is theirs from then on. */
const HAND_EVENTS = ['wheel', 'touchstart', 'pointerdown'] as const

/**
 * Scrolls the list the least that shows the current item and the ones after
 * it, if they are not in view already.
 */
const place = (
	container: HTMLElement,
	currentIndex: number,
	behavior: ScrollBehavior
): void => {
	// A smooth scroll still on its way stops where it is, so the items are
	// measured where the list really is and not where it is heading.
	container.scrollTo({ top: container.scrollTop, behavior: 'auto' })
	const top = agendaScrollTarget({
		containerTop: container.getBoundingClientRect().top + container.clientTop,
		containerHeight: container.clientHeight,
		scrollTop: container.scrollTop,
		items: Array.from(
			container.querySelectorAll<HTMLElement>(ITEM_SELECTOR),
			measure
		),
		currentIndex,
		lookahead: LOOKAHEAD,
		padding: PADDING,
	})
	if (top !== null) {
		container.scrollTo({ top, behavior })
	}
}

/**
 * Keeps the current agenda item, and the three after it, in view in a list
 * that scrolls on its own. It scrolls when the current item changes, and once
 * when the list first appears (without animation). Only the list moves - not
 * the page, not the focus.
 *
 * Right after it appears the list can still change size (the speakers
 * picker finds its devices, the window is resized): until the trainer takes
 * hold of the list - wheel, touch, scroll bar - such a change puts the
 * current item back in view, without animation. A list the trainer has
 * scrolled by hand is left alone until the current item changes again.
 *
 * The items are the container's elements marked `data-agenda-item`, in order;
 * `currentIndex` counts them, `-1` for none.
 */
export const useKeepCurrentInView = (
	containerRef: RefObject<HTMLElement>,
	currentIndex: number
): void => {
	const placed = useRef(false)

	useLayoutEffect(() => {
		const container = containerRef.current
		const animate = placed.current && !prefersReducedMotion()
		placed.current = true
		if (!container || currentIndex < 0) {
			return
		}
		place(container, currentIndex, animate ? 'smooth' : 'auto')

		if (typeof ResizeObserver === 'undefined') {
			return
		}
		let inTrainersHands = false
		const takeHold = (): void => {
			inTrainersHands = true
		}
		HAND_EVENTS.forEach(name =>
			container.addEventListener(name, takeHold, { passive: true })
		)
		// An observer reports the size it starts with: that is not a change.
		let reported = false
		const observer = new ResizeObserver(() => {
			if (reported && !inTrainersHands) {
				place(container, currentIndex, 'auto')
			}
			reported = true
		})
		observer.observe(container)
		return () => {
			observer.disconnect()
			HAND_EVENTS.forEach(name => container.removeEventListener(name, takeHold))
		}
	}, [containerRef, currentIndex])
}
